/**
 * StageResultOverlay — wood earned or drained, the play/clear WOOD bonus,
 * energy, lives left, stats, and the buttons the caller passes (NEXT,
 * TRY AGAIN, MENU...). ENTER presses the first button, ESC the last.
 */

import { FONT_SIZE, fontStyle } from "../FontScale";
import { fitText, stack } from "../view/layout";
import { PLAY_BONUS_WH } from "../../shared/stage/arcade";

const TITLES = {
  build: { complete: "DAM POWERED!", failed: "FLOODED OUT", rejected: "RUN NOT VERIFIED" },
  defend: { complete: "GRID DEFENDED", failed: "BLACKOUT", rejected: "RUN NOT VERIFIED" },
};
const fmt = (n) => Math.floor(n).toLocaleString("en-US");

/**
 * @param {object} r - StageSim result() with the (verified) woodLogs, woodDelta, totalWh
 * @param {{ note?: string, bonusWh?: number, lives?: number|null, actions: Array<{ label: string, color?: string, onPress: () => void }> }} opts
 */
export function showStageResult(scene, r, { note = "", bonusWh = 0, lives = null, actions }) {
  const W = scene._W;
  const CX = W / 2;
  const panelW = Math.min(460, W - 24);
  const maxW = panelW - 24;
  const top = scene._H * 0.18;
  const text = (content, size, color, extras) =>
    fitText(scene.add.text(CX, 0, content, fontStyle(size, color, extras)).setOrigin(0.5, 0).setDepth(402), maxW);

  const won = r.status === "complete";
  const deltaColor = r.woodDelta >= 0 ? "#7BC74A" : "#FF3333";
  const stats =
    r.kind === "build"
      ? `JUMPS ${r.jumpsCleared}  SMASHES ${r.smashes}  BUILT ${r.built}  HITS ${r.hits}`
      : `LEECHES PRIED ${r.pried}  JUMPS ${r.jumpsCleared}  HITS ${r.hits}`;
  const items = [
    text(titleFor(r), FONT_SIZE.TITLE, won ? "#FFD84A" : "#FF3333", { stroke: "#000000", strokeThickness: 4 }),
    text(`${fmt(r.woodLogs)} LOGS OF WOOD`, FONT_SIZE.HEADING, "#FFD84A"),
    text(`${r.woodDelta >= 0 ? "+" : ""}${fmt(r.woodDelta)} THIS STAGE`, FONT_SIZE.BODY, deltaColor),
    ...(bonusWh > 0 ? [text(bonusLine(bonusWh), FONT_SIZE.CAPTION, "#7BC74A")] : []),
    text(`${fmt(r.totalWh)} Wh OF ENERGY`, FONT_SIZE.CAPTION, "#00FFFF"),
    ...(lives !== null ? [text(lives > 0 ? `LIVES ${"♥".repeat(lives)}` : "NO LIVES LEFT", FONT_SIZE.BODY, lives > 0 ? "#FF6680" : "#FF3333")] : []),
    text(stats, FONT_SIZE.CAPTION, "#CCCCCC"),
    ...(note ? [text(note, FONT_SIZE.CAPTION, note === "VERIFIED" ? "#7BC74A" : "#FF6600")] : []),
    { space: 6 },
  ];
  actions.forEach(({ label, color = "#AAAAAA", onPress }) => {
    const b = text(label, FONT_SIZE.HEADING, color).setInteractive({ useHandCursor: true });
    b.on("pointerdown", onPress);
    items.push(b);
  });

  const panel = scene.add.graphics().setDepth(401);
  const bottom = stack(items, top + 16, 10);
  panel.fillStyle(0x000000, 0.88);
  panel.fillRoundedRect(CX - panelW / 2, top, panelW, bottom - top + 16, 10);
  panel.lineStyle(2, won ? 0xffd84a : 0xff3333, 0.8);
  panel.strokeRoundedRect(CX - panelW / 2, top, panelW, bottom - top + 16, 10);
  scene.overlayNextY = bottom + 28; // a payment gate goes below the panel
  if (actions.length > 0) {
    scene.input.keyboard.once("keydown-ENTER", actions[0].onPress);
    scene.input.keyboard.once("keydown-ESC", actions[actions.length - 1].onPress);
  }
}

/** A build stage lost to barrels is a knock-out; running out of reserve is the flood. */
function titleFor(r) {
  if (r.kind === "build" && r.status === "failed" && r.lives <= 0) return "KNOCKED OUT";
  return TITLES[r.kind][r.status];
}

/** "+50 LOGS FOR PLAYING  +100 CLEAR BONUS" from the bonus energy (10 Wh a log). */
function bonusLine(bonusWh) {
  const playLogs = PLAY_BONUS_WH / 10;
  const clearLogs = bonusWh / 10 - playLogs;
  return clearLogs > 0 ? `+${fmt(playLogs)} LOGS FOR PLAYING  +${fmt(clearLogs)} CLEAR BONUS` : `+${fmt(bonusWh / 10)} LOGS FOR PLAYING`;
}

/**
 * A short message panel with buttons (e.g. STAGE LOCKED). Pressing a button
 * removes the panel first.
 */
export function showStageMessage(scene, { title, lines = [], actions }) {
  const CX = scene._W / 2;
  const panelW = Math.min(460, scene._W - 24);
  const maxW = panelW - 24;
  const top = scene._H * 0.25;
  const parts = [];
  const text = (content, size, color) => {
    const t = fitText(scene.add.text(CX, 0, content, fontStyle(size, color)).setOrigin(0.5, 0).setDepth(402), maxW);
    parts.push(t);
    return t;
  };
  const items = [text(title, FONT_SIZE.TITLE, "#FFD84A"), ...lines.map((l) => text(l, FONT_SIZE.CAPTION, "#FFFFFF")), { space: 8 }];
  actions.forEach(({ label, onPress }) => {
    const b = text(label, FONT_SIZE.HEADING, "#7BC74A").setInteractive({ useHandCursor: true });
    b.on("pointerdown", () => {
      parts.forEach((p) => p.destroy());
      onPress();
    });
    items.push(b);
  });
  const panel = scene.add.graphics().setDepth(401);
  parts.push(panel);
  const bottom = stack(items, top + 16, 10);
  panel.fillStyle(0x000000, 0.88);
  panel.fillRoundedRect(CX - panelW / 2, top, panelW, bottom - top + 16, 10);
}
